import axios from 'axios';

import { getServerUrl } from '../constants';
import { getAuthTokenFromStorage, getUserIdFromStorage } from '../utils';

const SUBROUTE = 'race';

/**
 * @description gets race by id
 * @param {String} id race id
 * @returns {Promise<Object>} API response
 */
export const fetchRaceById = async (id) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/${id}`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets elections matching race
 * @param {String} id race id
 * @returns {Promise<Object>} API response
 */
export const fetchElectionsForRace = async (id) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/${id}/elections`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description creates a race object in the database
 * @param {Object} fields object with data to send
 * @param {String} fields.title title of race
 * @param {String} fields.position_description description of what the position is
 * @param {Object} fields.location location object with mandatory state field, then either county or city field filled in
 * @param {Array<String>} fields.candidates array of candidate ids in the election
 * @returns {Object} api response
 */
export const createRace = async (fields) => {
  const url = `${global.API_URL}/${SUBROUTE}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.post(url, {
      ...fields,
      created_by: getUserIdFromStorage(),
    }, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description adds candidateId to raceId
 * @param {String} raceId id of race
 * @param {String} candidateId id of candidate
 * @returns {Promise<Object>} API response
 */
export const addCandidateToRace = async (raceId, candidateId) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/add-candidate/${raceId}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.put(url, { candidateId }, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description sets topic meaning for race
 * @param {String} raceId id of race
 * @param {String} topic topic name
 * @param {String} meaning new topic meaning
 * @returns {Promise<Object>} API response
 */
export const setRaceTopicMeaning = async (raceId, topic, meaning) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/topic-meaning/${raceId}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.put(url, { topic, meaning }, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description approves a race object
 * @param {String} id id of race object
 * @returns {Promise<Object>} API response
 */
export const adminApproveRace = async (id) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/admin/approve/${id}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.get(url, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description unapproves a race object
 * @param {String} id id of race object
 * @returns {Promise<Object>} API response
 */
export const adminUnapproveRace = async (id) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/admin/unapprove/${id}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.get(url, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets all races in the future that are approved
 * @returns {Promise<Object>} API response
 */
export const fetchAllApprovedFutureRaces = async () => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/future/approved`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets all races in the future that are unapproved
 * @returns {Promise<Object>} API response
 */
export const fetchAllUnapprovedFutureRaces = async () => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/future/unapproved`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
